import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { loadCategories } from '../../utils/categories';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminCategories() {
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get('return');
  const bookReturn = /^\/admin\/books\/[a-f0-9]{24}$/i.test(returnTo || '') ? returnTo : null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // { name, draft }

  const ping = (m) => { setToast(m); setTimeout(() => setToast(''), 1800); };

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get('/api/admin/categories', { headers: authHeaders() });
      setItems(Array.isArray(data) ? data : []);
      // Keep the public cache in sync so home/category pages reflect changes
      // without needing a hard refresh.
      loadCategories(true);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true); setErr('');
    try {
      await axios.post('/api/admin/categories', { name }, { headers: authHeaders() });
      setNewName('');
      ping('Created');
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const startRename = (c) => setEditing({ name: c.name, draft: c.name });
  const cancelRename = () => setEditing(null);
  const commitRename = async () => {
    if (!editing) return;
    const target = editing.draft.trim();
    if (!target || target === editing.name) { setEditing(null); return; }
    try {
      const { data } = await axios.put(
        `/api/admin/categories/${encodeURIComponent(editing.name)}`,
        { name: target },
        { headers: authHeaders() }
      );
      ping(data.moved ? `Renamed (${data.moved} books moved)` : 'Renamed');
      setEditing(null);
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Rename failed');
    }
  };

  const onDelete = async (c) => {
    const fallback = c.count > 0
      ? window.prompt(`"${c.name}" has ${c.count} book${c.count === 1 ? '' : 's'}. Move them to which category?`, 'General')
      : 'General';
    if (fallback === null) return; // cancelled
    const trimmed = (fallback || 'General').trim();
    if (!trimmed) return;
    try {
      const { data } = await axios.delete(
        `/api/admin/categories/${encodeURIComponent(c.name)}?fallback=${encodeURIComponent(trimmed)}`,
        { headers: authHeaders() }
      );
      ping(data.moved ? `Deleted (${data.moved} books moved to "${trimmed}")` : 'Deleted');
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed');
    }
  };

  const totalBooks = items.reduce((s, c) => s + (c.count || 0), 0);

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1 className="admin-page-title">Categories</h1>
        {bookReturn && <Link className="admin-btn admin-btn-ghost admin-btn-sm" to={bookReturn}>← Back to book</Link>}
        <p className="admin-page-sub">{items.length} categories · {totalBooks} books across them.</p>
      </header>

      <div className="admin-card">
        <h2>Add a category</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div className="admin-field" style={{ flex: 1, marginBottom: 0 }}>
            <label className="admin-label">Name</label>
            <input
              className="admin-input"
              value={newName}
              placeholder="e.g. Adventure, Faith, Bedtime"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
            />
          </div>
          <button type="button" className="admin-btn" disabled={!newName.trim() || creating} onClick={create}>
            {creating ? 'Creating…' : '+ Add'}
          </button>
        </div>
      </div>

      {err && <div className="admin-toast admin-toast-error">{err}</div>}
      {toast && <div className="admin-toast">{toast}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Books</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={3} style={{ textAlign: 'center', padding: 30, opacity: 0.7 }}>No categories yet.</td></tr>
            ) : items.map((c) => {
              const isEditing = editing && editing.name === c.name;
              return (
                <tr key={c.name}>
                  <td>
                    {isEditing ? (
                      <input
                        className="admin-input"
                        autoFocus
                        value={editing.draft}
                        onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') cancelRename(); }}
                      />
                    ) : (
                      <strong style={{ color: 'var(--admin-fg-strong)' }}>{c.name}</strong>
                    )}
                  </td>
                  <td>{c.count || 0}</td>
                  <td>
                    <div className="admin-row-actions">
                      {isEditing ? (
                        <>
                          <button type="button" className="admin-btn admin-btn-sm" onClick={commitRename}>Save</button>
                          <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={cancelRename}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => startRename(c)}>Rename</button>
                          <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => onDelete(c)}>Delete</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
